import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-document-folder',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="p-5 min-h-full bg-[#F7FAFC] overflow-y-auto">
      <div class="max-w-[1300px] mx-auto space-y-5">
        <header class="flex flex-wrap items-start justify-between gap-4">
          <div class="min-w-0">
            <a routerLink="/admin/documents" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1">
              <i class="ti ti-arrow-left"></i>Folders
            </a>
            <h1 class="text-xl font-semibold text-slate-900 tracking-tight truncate">{{ selectedFolder()?.name || 'Folder' }}</h1>
            <p class="text-slate-500 text-xs mt-1">{{ selectedFolder()?.description || 'Upload and manage documents inside this folder.' }}</p>
          </div>
          <div class="flex flex-wrap gap-2">
            @if (selectedFolder()) {
              <button type="button" (click)="openFolderModal()" class="h-10 px-4 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50 flex items-center gap-2">
                <i class="ti ti-pencil text-base"></i>Edit Folder
              </button>
              <button type="button" (click)="deleteFolder()" class="h-10 px-4 bg-rose-50 border border-rose-100 text-rose-700 rounded-lg text-xs font-semibold hover:bg-rose-100 flex items-center gap-2">
                <i class="ti ti-trash text-base"></i>Delete Folder
              </button>
            }
            <button type="button" (click)="openUploadModal()" class="h-10 px-4 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 flex items-center gap-2" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
              <i class="ti ti-upload text-base"></i>Upload Document
            </button>
          </div>
        </header>

        <section class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div class="summary-card"><p>Documents</p><strong>{{ selectedDocuments().length }}</strong></div>
          <div class="summary-card"><p>Filtered</p><strong class="text-[#1A6FA6]">{{ filteredDocuments().length }}</strong></div>
          <div class="summary-card"><p>Storage</p><strong class="text-slate-700">{{ folderSizeLabel() }}</strong></div>
        </section>

        <section class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100">
            <div class="relative max-w-md">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
              <input type="text" placeholder="Search documents..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)"
                     class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:outline-none focus:bg-white focus:border-[#1A6FA6]">
            </div>
          </div>

          @if (isLoading()) {
            <div class="p-12 text-center text-sm text-slate-500"><i class="ti ti-loader animate-spin"></i> Loading documents...</div>
          } @else if (!selectedFolder()) {
            <div class="p-12 text-center">
              <i class="ti ti-folder-off text-4xl text-slate-300"></i>
              <p class="text-sm font-semibold text-slate-900 mt-2">Folder not found</p>
            </div>
          } @else {
            <div class="divide-y divide-slate-100">
              @for (doc of filteredDocuments(); track doc.id) {
                <div class="px-4 py-3 flex flex-wrap items-center justify-between gap-3 hover:bg-slate-50/70">
                  <div class="flex items-center gap-3 min-w-0">
                    <span class="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 text-slate-500 flex items-center justify-center shrink-0"><i class="ti" [ngClass]="fileIcon(doc)"></i></span>
                    <div class="min-w-0">
                      <p class="text-sm font-semibold text-slate-900 truncate">{{ doc.title }}</p>
                      <p class="text-xs text-slate-500 truncate">{{ doc.original_name }} · {{ fileSizeLabel(doc.file_size) }} · {{ doc.uploaded_at | date:'d MMM y' }}</p>
                      @if (doc.notes) {
                        <p class="text-xs text-slate-500 truncate mt-0.5">{{ doc.notes }}</p>
                      }
                    </div>
                  </div>
                  <div class="flex flex-wrap gap-1.5">
                    <a [href]="documentUrl(doc)" target="_blank" rel="noopener" class="h-8 px-3 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] text-xs font-semibold inline-flex items-center gap-1"><i class="ti ti-external-link"></i> Open</a>
                    <button type="button" (click)="deleteDocument(doc)" class="h-8 px-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs font-semibold"><i class="ti ti-trash"></i> Delete</button>
                  </div>
                </div>
              } @empty {
                <div class="p-12 text-center">
                  <i class="ti ti-file-upload text-4xl text-slate-300"></i>
                  <p class="text-sm font-semibold text-slate-900 mt-2">No documents in this folder</p>
                  <p class="text-xs text-slate-500 mt-1">Upload a PDF, Word, Excel, CSV, text, or image file.</p>
                </div>
              }
            </div>
          }
        </section>

        @if (showFolderModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-100 overflow-hidden">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <h2 class="text-lg font-semibold text-slate-900">Edit Folder</h2>
                <button type="button" (click)="showFolderModal.set(false)" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"><i class="ti ti-x text-lg"></i></button>
              </div>
              <div class="p-5 space-y-4">
                <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Folder Name</span><input type="text" [(ngModel)]="folderForm.name" class="field-input"></label>
                <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Description</span><textarea [(ngModel)]="folderForm.description" rows="3" class="field-input h-auto py-2 resize-none"></textarea></label>
              </div>
              <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                <button type="button" (click)="showFolderModal.set(false)" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200">Cancel</button>
                <button type="button" (click)="saveFolder()" [disabled]="isSaving() || !folderForm.name.trim()" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50">
                  {{ isSaving() ? 'Saving...' : 'Save Folder' }}
                </button>
              </div>
            </div>
          </div>
        }

        @if (showUploadModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-100 overflow-hidden">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">Upload Document</h2>
                  <p class="text-xs text-slate-500 mt-1">{{ selectedFolder()?.name }}</p>
                </div>
                <button type="button" (click)="closeUploadModal()" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"><i class="ti ti-x text-lg"></i></button>
              </div>
              <div class="p-5 space-y-4">
                <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Title</span><input type="text" [(ngModel)]="uploadForm.title" class="field-input" placeholder="Leave blank to use filename"></label>
                <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Notes</span><textarea [(ngModel)]="uploadForm.notes" rows="3" class="field-input h-auto py-2 resize-none"></textarea></label>
                <label class="block rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center cursor-pointer hover:bg-white">
                  <i class="ti ti-cloud-upload text-3xl text-[#1A6FA6]"></i>
                  <span class="block text-sm font-semibold text-slate-900 mt-2">{{ selectedFileName || 'Choose document' }}</span>
                  <span class="block text-xs text-slate-500 mt-1">PDF, Word, Excel, CSV, text, or image. Max 25MB.</span>
                  <input type="file" class="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/jpeg,image/png,image/webp" (change)="onDocumentSelected($event)">
                </label>
              </div>
              <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                <button type="button" (click)="closeUploadModal()" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200">Cancel</button>
                <button type="button" (click)="uploadDocument()" [disabled]="isUploading() || !selectedFile" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50">
                  {{ isUploading() ? 'Uploading...' : 'Upload' }}
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .summary-card { border: 1px solid #E7EEF5; border-radius: 0.75rem; background: #fff; padding: 0.75rem 1rem; }
    .summary-card p { font-size: 0.625rem; text-transform: uppercase; letter-spacing: 0.12em; font-weight: 700; color: #94a3b8; }
    .summary-card strong { display: block; margin-top: 0.25rem; font-size: 1.25rem; font-weight: 700; color: #0f172a; }
    .field-input { width: 100%; min-height: 2.5rem; border-radius: 0.5rem; border: 1px solid #e2e8f0; background: #f8fafc; padding: 0 0.75rem; font-size: 0.875rem; color: #1e293b; outline: none; }
    .field-input:focus { border-color: #1A6FA6; background: #fff; }
  `]
})
export class AdminDocumentFolderComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  folders = signal<any[]>([]);
  documents = signal<any[]>([]);
  searchQuery = signal('');
  isLoading = signal(true);
  isSaving = signal(false);
  isUploading = signal(false);
  showFolderModal = signal(false);
  showUploadModal = signal(false);

  folderId = 0;
  folderForm = { id: 0, name: '', description: '' };
  uploadForm = { title: '', notes: '' };
  selectedFile: File | null = null;
  selectedFileName = '';

  selectedFolder = computed(() => this.folders().find(folder => Number(folder.id) === Number(this.folderId)) || null);
  selectedDocuments = computed(() => this.documents().filter(doc => Number(doc.folder_id) === Number(this.folderId)));
  filteredDocuments = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    return this.selectedDocuments().filter(doc => !query || [doc.title, doc.original_name, doc.notes, doc.uploaded_by_name].some(value => String(value || '').toLowerCase().includes(query)));
  });

  ngOnInit() {
    this.folderId = Number(this.route.snapshot.paramMap.get('id') || 0);
    this.loadLibrary();
  }

  loadLibrary() {
    this.isLoading.set(true);
    this.api.getDocumentLibrary().subscribe({
      next: (res: any) => {
        this.folders.set(res.success && res.data?.folders ? res.data.folders : []);
        this.documents.set(res.success && res.data?.documents ? res.data.documents : []);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Failed to load documents.');
      }
    });
  }

  folderSizeLabel(): string {
    return this.fileSizeLabel(this.selectedDocuments().reduce((sum, doc) => sum + Number(doc.file_size || 0), 0));
  }

  openFolderModal() {
    const folder = this.selectedFolder();
    if (!folder) return;
    this.folderForm = { id: Number(folder.id), name: folder.name || '', description: folder.description || '' };
    this.showFolderModal.set(true);
  }

  saveFolder() {
    if (!this.folderForm.name.trim()) return;
    this.isSaving.set(true);
    this.api.saveDocumentFolder({
      id: this.folderForm.id,
      name: this.folderForm.name.trim(),
      description: this.folderForm.description || ''
    }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Folder saved.');
          this.showFolderModal.set(false);
          this.loadLibrary();
        } else {
          showToast('error', res.message || 'Failed to save folder.');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Network error while saving folder.');
      }
    });
  }

  deleteFolder() {
    const folder = this.selectedFolder();
    if (!folder?.id) return;
    if (!window.confirm(`Delete "${folder.name}" and all documents inside it?`)) return;
    this.api.deleteDocumentFolder(Number(folder.id)).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Folder deleted.');
          this.router.navigate(['/admin/documents']);
        } else {
          showToast('error', res.message || 'Failed to delete folder.');
        }
      },
      error: (err) => showToast('error', err?.error?.message || 'Network error while deleting folder.')
    });
  }

  openUploadModal() {
    if (!this.selectedFolder()) {
      showToast('error', 'Folder not found.');
      return;
    }
    this.uploadForm = { title: '', notes: '' };
    this.selectedFile = null;
    this.selectedFileName = '';
    this.showUploadModal.set(true);
  }

  closeUploadModal() {
    this.showUploadModal.set(false);
    this.selectedFile = null;
    this.selectedFileName = '';
  }

  onDocumentSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      showToast('error', 'Document must be under 25MB.');
      input.value = '';
      return;
    }
    this.selectedFile = file;
    this.selectedFileName = file.name;
  }

  uploadDocument() {
    if (!this.folderId || !this.selectedFile) return;
    this.isUploading.set(true);
    const formData = new FormData();
    formData.append('folder_id', String(this.folderId));
    formData.append('title', this.uploadForm.title || '');
    formData.append('notes', this.uploadForm.notes || '');
    formData.append('document', this.selectedFile);

    this.api.uploadLibraryDocument(formData).subscribe({
      next: (res: any) => {
        this.isUploading.set(false);
        if (res.success) {
          showToast('success', 'Document uploaded.');
          this.closeUploadModal();
          this.loadLibrary();
        } else {
          showToast('error', res.message || 'Failed to upload document.');
        }
      },
      error: (err) => {
        this.isUploading.set(false);
        showToast('error', err?.error?.message || 'Network error while uploading document.');
      }
    });
  }

  deleteDocument(doc: any) {
    if (!doc?.id) return;
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    this.api.deleteLibraryDocument(Number(doc.id)).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Document deleted.');
          this.loadLibrary();
        } else {
          showToast('error', res.message || 'Failed to delete document.');
        }
      },
      error: (err) => showToast('error', err?.error?.message || 'Network error while deleting document.')
    });
  }

  documentUrl(doc: any): string {
    return this.api.assetUrl(doc?.file_url || '');
  }

  fileIcon(doc: any): string {
    const name = String(doc?.original_name || '').toLowerCase();
    if (name.endsWith('.pdf')) return 'ti-file-type-pdf';
    if (name.endsWith('.doc') || name.endsWith('.docx')) return 'ti-file-type-doc';
    if (name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv')) return 'ti-file-spreadsheet';
    if (/\.(jpg|jpeg|png|webp)$/.test(name)) return 'ti-photo';
    return 'ti-file-description';
  }

  fileSizeLabel(size: any): string {
    const bytes = Number(size || 0);
    if (bytes <= 0) return '0 KB';
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
}
